// Dialogs opened from the user menu: sign-out confirmation, profile, security
// (password + two-factor). All of them call the real /auth endpoints.
import { useRef, useState } from 'react';
import Modal, { Field } from './Modal.jsx';
import { Icon } from './Icon.jsx';
import { MIN_PASSWORD, useAuth } from '../lib/auth.jsx';
import { patch, post } from '../lib/api.js';
import { errorMessage } from '../lib/errors.js';
import { toast } from '../lib/toast.jsx';
import { useT } from '../lib/i18n.jsx';

export function LogoutDialog({ onClose, returnFocusRef }) {
  const t = useT();
  const { logout } = useAuth();
  const cancelRef = useRef(null);
  const [busy, setBusy] = useState(false);

  // logout() revokes the tokens server-side first; it never throws.
  const signOut = async () => {
    setBusy(true);
    await logout();
    toast(t('account.logout.done'));
  };
  return (
    <Modal
      onClose={onClose}
      className="modal--danger"
      maxWidth={440}
      initialFocusRef={cancelRef}
      returnFocusRef={returnFocusRef}
      title={t('account.logout.title')}
      description={t('account.logout.body')}
      footer={
        <>
          <button ref={cancelRef} type="button" className="btn-ghost" onClick={onClose}>
            {t('g.cancel')}
          </button>
          <button
            type="button"
            className="btn-danger"
            disabled={busy}
            onClick={signOut}
          >
            {busy ? t('account.working') : t('account.logout.cta')}
          </button>
        </>
      }
    />
  );
}

export function AccountDialog({ lang, onClose, returnFocusRef }) {
  const t = useT();
  const { user, refreshMe } = useAuth();
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [currentPw, setCurrentPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const emailChanged = email.trim().toLowerCase() !== (user?.email || '').toLowerCase();

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      const body = { full_name: fullName.trim() };
      if (emailChanged) {
        body.email = email.trim();
        body.current_password = currentPw;
      }
      await patch('/auth/me', body);
      await refreshMe();
      toast(t('account.profile.saved'));
      onClose();
    } catch (e2) {
      setErr(errorMessage(e2, { lang }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      as="form"
      onSubmit={submit}
      onClose={onClose}
      maxWidth={480}
      returnFocusRef={returnFocusRef}
      title={t('account.title')}
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>
            {t('g.cancel')}
          </button>
          <button type="submit" className="btn-primary" disabled={busy}>
            {busy ? t('account.working') : t('g.save')}
          </button>
        </>
      }
    >
      <div className="modal-body">
        {err && <div className="login-error" role="alert">{err}</div>}
        <div className="login-form">
          <Field label={t('account.profile.fullName')}>
            <input required autoComplete="name" value={fullName} onChange={(e) => setFullName(e.target.value)} />
          </Field>
          <Field label={t('account.profile.email')}>
            <input required type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          {emailChanged && (
            <Field
              label={t('account.currentPassword')}
              hint={t('account.profile.currentPasswordHint')}
            >
              <input
                required
                type="password"
                autoComplete="current-password"
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
              />
            </Field>
          )}
        </div>
      </div>
    </Modal>
  );
}

export function SecurityDialog({ lang, onClose, returnFocusRef }) {
  const t = useT();
  const { user } = useAuth();
  const [sub, setSub] = useState(null); // 'password' | 'mfa-setup' | 'mfa-disable'
  const mfa = !!user?.mfa_enabled;

  return (
    <>
      <Modal
        onClose={onClose}
        maxWidth={520}
        returnFocusRef={returnFocusRef}
        title={t('account.security.title')}
        description={t('account.security.description')}
        footer={
          <button type="button" className="btn-ghost" onClick={onClose}>
            {t('account.close')}
          </button>
        }
      >
        <div className="modal-body">
          <section className="sec-row">
            <div>
              <div className="sec-row__title">{t('account.password')}</div>
              <div className="sec-row__sub">{t('account.security.passwordSub')}</div>
            </div>
            <button type="button" className="btn-ghost" onClick={() => setSub('password')}>
              {t('account.change')}
            </button>
          </section>
          <section className="sec-row">
            <div>
              <div className="sec-row__title">{t('account.security.mfa')}</div>
              <div className="sec-row__sub">
                {mfa ? t('account.security.mfaActive') : t('account.security.mfaOff')}
              </div>
            </div>
            {mfa ? (
              <button type="button" className="btn-ghost" onClick={() => setSub('mfa-disable')}>
                {t('account.disable')}
              </button>
            ) : (
              <button type="button" className="btn-primary" onClick={() => setSub('mfa-setup')}>
                {t('account.enable')}
              </button>
            )}
          </section>
        </div>
      </Modal>
      {sub === 'password' && <ChangePasswordDialog lang={lang} onClose={() => setSub(null)} />}
      {sub === 'mfa-setup' && <MfaSetupDialog lang={lang} onClose={() => setSub(null)} />}
      {sub === 'mfa-disable' && <MfaDisableDialog lang={lang} onClose={() => setSub(null)} />}
    </>
  );
}


function ChangePasswordDialog({ lang, onClose }) {
  const t = useT();
  const [cur, setCur] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (pw.length < MIN_PASSWORD) {
      setErr(t('account.password.min', { n: MIN_PASSWORD }));
      return;
    }
    if (pw !== pw2) {
      setErr(t('account.password.mismatch'));
      return;
    }
    setBusy(true);
    try {
      await post('/auth/change-password', { current_password: cur, new_password: pw });
      toast(t('account.password.changed'));
      onClose();
    } catch (e2) {
      setErr(
        e2?.status === 400
          ? t('account.password.wrongCurrent')
          : errorMessage(e2, { lang }),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      as="form"
      onSubmit={submit}
      onClose={onClose}
      maxWidth={460}
      title={t('account.password.changeTitle')}
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>
            {t('g.cancel')}
          </button>
          <button type="submit" className="btn-primary" disabled={busy}>
            {busy ? t('account.working') : t('account.change')}
          </button>
        </>
      }
    >
      <div className="modal-body">
        {err && <div className="login-error" role="alert">{err}</div>}
        <div className="login-form">
          <Field label={t('account.currentPassword')}>
            <input required type="password" autoComplete="current-password" value={cur} onChange={(e) => setCur(e.target.value)} />
          </Field>
          <Field
            label={t('account.password.new')}
            hint={t('account.password.min', { n: MIN_PASSWORD })}
          >
            <input required type="password" autoComplete="new-password" minLength={MIN_PASSWORD} value={pw} onChange={(e) => setPw(e.target.value)} />
          </Field>
          <Field label={t('account.password.confirm')}>
            <input required type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} />
          </Field>
        </div>
      </div>
    </Modal>
  );
}

function MfaSetupDialog({ lang, onClose }) {
  const t = useT();
  const { refreshMe } = useAuth();
  const [setup, setSetup] = useState(null); // { secret, provisioning_uri }
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setErr('');
    setBusy(true);
    try {
      setSetup(await post('/auth/mfa/setup', {}));
    } catch (e2) {
      setErr(errorMessage(e2, { lang }));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (e) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      const res = await post('/auth/mfa/confirm', { code: code.replace(/\s/g, '') });
      setBackupCodes(res?.backup_codes || []);
      await refreshMe();
    } catch (e2) {
      setErr(errorMessage(e2, { lang, context: 'mfa' }));
    } finally {
      setBusy(false);
    }
  };

  if (backupCodes) {
    return (
      <Modal
        onClose={onClose}
        maxWidth={480}
        title={t('account.mfa.enabledTitle')}
        description={t('account.mfa.recoveryBody')}
        footer={
          <button type="button" className="btn-primary" onClick={onClose}>
            {t('account.mfa.saved')}
          </button>
        }
      >
        <div className="modal-body">
          <div className="code-list">
            {backupCodes.map((c) => (
              <span key={c}>{c}</span>
            ))}
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      as="form"
      onSubmit={setup ? confirm : (e) => { e.preventDefault(); start(); }}
      onClose={onClose}
      maxWidth={480}
      title={t('account.mfa.enableTitle')}
      description={setup ? t('account.mfa.setupBody') : t('account.mfa.introBody')}
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>
            {t('g.cancel')}
          </button>
          <button type="submit" className="btn-primary" disabled={busy}>
            {busy ? t('account.working') : setup ? t('g.confirm') : t('account.continue')}
          </button>
        </>
      }
    >
      {(err || setup) && (
        <div className="modal-body">
          {err && <div className="login-error" role="alert">{err}</div>}
          {setup && (
            <div className="login-form">
              <div>
                <div className="field-label">{t('account.mfa.key')}</div>
                <div className="secret-box">{setup.secret}</div>
                <p className="field-hint">
                  <a href={setup.provisioning_uri} style={{ textDecoration: 'underline' }}>
                    {t('account.mfa.openInApp')}
                  </a>
                  {' '}
                  <button
                    type="button"
                    className="user-copy"
                    title={t('account.mfa.copyKey')}
                    aria-label={t('account.mfa.copyKey')}
                    onClick={() => navigator.clipboard?.writeText(setup.secret).then(() => toast(t('account.copied')))}
                  >
                    <Icon name="copy" size={11} />
                  </button>
                </p>
              </div>
              <Field label={t('account.mfa.codeFromApp')}>
                <input
                  required
                  autoFocus
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9 ]{6,8}"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </Field>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function MfaDisableDialog({ lang, onClose }) {
  const t = useT();
  const { refreshMe } = useAuth();
  const [code, setCode] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      await post('/auth/mfa/disable', { code: code.replace(/\s/g, '') });
      await refreshMe();
      toast(t('account.mfa.disabled'));
      onClose();
    } catch (e2) {
      setErr(errorMessage(e2, { lang, context: 'mfa' }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      as="form"
      onSubmit={submit}
      onClose={onClose}
      className="modal--danger"
      maxWidth={460}
      title={t('account.mfa.disableTitle')}
      description={t('account.mfa.disableBody')}
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>
            {t('g.cancel')}
          </button>
          <button type="submit" className="btn-danger" disabled={busy}>
            {busy ? t('account.working') : t('account.disable')}
          </button>
        </>
      }
    >
      <div className="modal-body">
        {err && <div className="login-error" role="alert">{err}</div>}
        <div className="login-form">
          <Field label={t('account.mfa.code')}>
            <input required inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} />
          </Field>
        </div>
      </div>
    </Modal>
  );
}
